import type { MedicationLabelData } from "@/lib/medication-label";

import { Unit } from "@/types/enums/unit";

import type { ItemStockDetail } from "./inventory/item";
import { ItemTag } from "./item-tag";
import { UserTiny } from "./user";

export interface DoseShipmentAllocation {
  shipmentId: string;
  quantity: number;
  allowExtraYield?: boolean;
  /** Request-only physical plan binding, checked against fresh server conversion. */
  expectedStockId?: string;
  expectedStockQuantity?: number;
  expectedStockUnit?: Unit | null;
}

export interface DoseInventoryLot {
  shipmentId: string;
  stockId: string;
  lotNumber: string | null;
  quantity: number;
  unit: Unit | null;
  expiryDate: string | null;
}

export enum DoseLotSelectionMethod {
  AUTOMATIC = "AUTOMATIC",
  EXPLICIT = "EXPLICIT",
  UNKNOWN = "UNKNOWN",
}

export interface DoseLot {
  id: string;
  shipmentId: string | null;
  stockId: string | null;
  lotNumber: string | null;
  expiryDate: string | null;
  selectionMethod: DoseLotSelectionMethod;
}

export interface DoseComponent {
  id?: string;
  medicationId?: string;
  name: string;
  amount?: number;
  unit?: Unit | null;
  quantity?: number;
  /** Omitted on save takes the medication's default dilution. */
  dilutionId?: string | null;
  /** mL of diluent per vial when the dose was drawn: 2.5 reads "2.5 mL". */
  diluentVolume?: number | null;
  vialStrength?: number | null;
  lots?: DoseLot[];
  /** Request-only: omitted preserves documentation, [] clears it. */
  untrackedLots?: string[];
  stockId?: string;
  /** Actual positive net ledger use, never a future allocation or charted-dose estimate. */
  inventoryLots?: DoseInventoryLot[];
  shipmentAllocations?: DoseShipmentAllocation[];
  /** Set once the part was checked out; its stock stays as the sale took it. */
  chargeItemId?: string | null;
  /** Request-only quantity-free receipt references for unsold checkout-timed doses. */
  clinicalShipmentIds?: string[];
  /** Request-only: true claims immutable checkout use, false records a physical plan. */
  reuseCheckoutInventory?: boolean;
  /** Request-only explicit correction of an existing sale's original inventory use. */
  correctActualInventoryUse?: boolean;
  allowExtraYield?: boolean;
  locationId?: string;
  isArchived?: boolean;
}

export interface CompoundIngredient {
  id?: string;
  ingredientMedicationId: string;
  ingredientName?: string | null;
  amount?: number | null;
  unit?: Unit | null;
  quantity?: number;
  position?: number;
}

/** A reconstitution: mL of diluent added to a vial holding `vialStrength` of the medication. */
export interface MedicationDilution {
  id?: string;
  diluentVolume: number;
  vialStrength?: number | null;
  isDefault?: boolean;
  position?: number;
  label?: string;
}

export type MedicationStatus = "active" | "inactive" | "deleted";

/** When a medication billed in different units than it's charted in takes stock out. */
export type InventoryDrawdownTiming = "DOSE" | "CHECKOUT";

// Staff-set clinical management status of a dose (distinct from the
// webhook-managed DoseSpot lifecycle `status`).
export enum DoseManagementStatus {
  RECOMMENDED = "recommended",
  RECOMMENDED_PATIENT_SOURCED = "recommended_patient_sourced",
  ACTIVE = "active",
  DISCONTINUED = "discontinued",
}

/** One place on the body a dose was given, with the amount given there when known. */
export interface DoseLocation {
  id?: string;
  label: string;
  /** Stable key for a face chart muscle; empty for a typed location. */
  muscle?: string | null;
  amount?: number | null;
  /** "chart" rows are kept current by the face chart; "manual" rows are typed. */
  source?: string;
  position?: number;
  /** Component (lot and reconstitution) this site was drawn from; none means the first. */
  doseComponentId?: string | null;
  /** On save: index into the request's components of the one this site was drawn from. */
  componentPosition?: number | null;
}

export interface MedicationCreate {
  patientId: string;
  providerId?: string;
  sideEffects?: string;
  administrationLocation?: string;
  locations?: DoseLocation[];
  components: DoseComponent[];
  locationId?: string;
  noteId?: string;
  /** Appointment the dose was given at (checklist Add Dose, or a note linked to one). */
  eventId?: string | null;
  doseDate?: string;
  comment?: string;
  frequency?: string;
  signedOffProviderId?: string;
  signedOffDate?: string;
  managementStatus?: DoseManagementStatus | null;
  compoundMedicationId?: string | null;
}

export interface MedicationUpdate {
  sideEffects?: string;
  administrationLocation?: string;
  /** Omitted leaves the dose's locations as they are; an empty list clears them. */
  locations?: DoseLocation[];
  components: DoseComponent[];
  providerId?: string;
  doseDate?: string;
  comment?: string;
  frequency?: string;
  signedOffProviderId?: string;
  signedOffDate?: string;
  managementStatus?: DoseManagementStatus | null;
  compoundMedicationId?: string | null;
}

export interface MedicationSummary extends MedicationCreate {
  id: string;
  createdDate: string;
  provider?: UserTiny;
  createdBy?: UserTiny | null;
  dosespotMedicationId?: string | null; // DoseSpot Medication ID for e-prescribing integration
  status?: MedicationStatus | null; // DoseSpot lifecycle status (webhook-managed; read-only)
  // Pharmacy the DoseSpot prescription was sent to (webhook-managed; read-only).
  dosespotPharmacyId?: number | null;
  pharmacyName?: string | null;
  pharmacyAddress?: string | null;
  pharmacyPhone?: string | null;
  // Rx label printing: reprintable snapshot + who/when it was last printed.
  labelData?: MedicationLabelData | null;
  labelPrintedDate?: string | null;
  labelPrintedProviderId?: string | null;
  compoundMedicationId?: string | null;
  compoundChargeItemId?: string | null;
  /** Face chart attachment this dose was charted on; null for any other dose. */
  chartAttachmentId?: string | null;
}

/** Doses a face chart stopped linking to when it was moved or deleted. */
export interface DetachedChartDoses {
  doseIds: string[];
  comment: string;
}

export interface MedicationTemplate {
  id: string;
  name: string;
  amount: number | null;
  unit: Unit | null;
  administrationLocation: string | null;
  frequency: string | null;
  quantity: number;
  comment: string | null;
  lotNumber: string | null;
}

export interface Medication {
  id: string;
  name: string;
  /** Another name staff search by and see at checkout; never shown to patients. Read via itemStaffName(). */
  alias?: string | null;
  /** The enterprise's record this one follows, if any. */
  enterpriseRef?: string | null;
  unit: string | null;
  brandName: string | null;
  description: string | null;
  price: number;
  cost: number | null;
  image?: string | null;
  imageUrl?: string | null;
  category: string;
  internalNotes: string | null;
  sortOrder: number | null;
  isUsedAsTemplate: boolean;
  createdDate: string;
  updatedDate: string | null;
  isArchived: boolean;
  allowCustomPricing: boolean;
  commissionable: boolean;
  showInCheckOut: boolean;
  template: MedicationTemplate | null;
  isCompound: boolean;
  /** Badged and listed first in the online shop. */
  isPopular?: boolean;
  /** Shows the Quantity box when charting a dose; without it doses save with quantity 1. */
  showDoseQuantity?: boolean;
  ingredients: CompoundIngredient[];
  dilutions?: MedicationDilution[];
  /** Charted units per billed unit: 3 bills a 60-unit dose as 20. */
  billingUnitRatio?: number;
  inventoryDrawdownTiming?: InventoryDrawdownTiming;
  externalId?: string | null;
  exemptedTaxIds?: string[];
  defaultCptCode?: string | null;
  defaultModifiers?: string[] | null;
  defaultIcd10Codes?: string[] | null;
  defaultPlaceOfService?: string | null;
  stocks?: ItemStockDetail[];
  tags?: ItemTag[];
  lastDoseDate?: string | null;
  doseCount?: number;
}

export interface MedicationMergeSide {
  id: string;
  name: string;
  unit: string | null;
  isArchived: boolean;
  doseCount: number;
  lastDoseDate: string | null;
  chargeItemCount: number;
  templateCount: number;
  carePlanStepCount: number;
  carePlanTemplateStepCount: number;
  stockLinkCount: number;
  inventoryLogCount: number;
  compoundLinkCount?: number;
}

export interface MergeMedicationsRequest {
  keepId: string;
  sourceIds: string[];
}

export interface MergeMedicationsPreview {
  keep: MedicationMergeSide;
  sources: MedicationMergeSide[];
}

export interface CreateMedicationData {
  name: string;
  /** Another name staff search by and see at checkout; never shown to patients. Read via itemStaffName(). */
  alias?: string | null;
  unit: string | null;
  brandName?: string | null;
  description?: string | null;
  price: number;
  cost?: number | null;
  category?: string;
  internalNotes?: string | null;
  sortOrder?: number | null;
  allowCustomPricing?: boolean;
  commissionable?: boolean;
  showInCheckOut?: boolean;
  isUsedAsTemplate?: boolean;
  isCompound?: boolean;
  isPopular?: boolean;
  showDoseQuantity?: boolean;
  ingredients?: CompoundIngredient[];
  dilutions?: MedicationDilution[];
  billingUnitRatio?: number;
  inventoryDrawdownTiming?: InventoryDrawdownTiming;
  externalId?: string | null;
  defaultCptCode?: string | null;
  defaultModifiers?: string[] | null;
  defaultIcd10Codes?: string[] | null;
  defaultPlaceOfService?: string | null;
  // Template fields (only used when isUsedAsTemplate is true)
  templateAmount?: number | null;
  templateAdministrationLocation?: string | null;
  templateFrequency?: string | null;
  templateQuantity?: number | null;
  templateComment?: string | null;
  templateLotNumber?: string | null;
  exemptedTaxIds?: string[];
}

export interface UpdateMedicationData {
  name?: string;
  /** Another name staff search by and see at checkout; never shown to patients. Read via itemStaffName(). */
  alias?: string | null;
  unit?: string | null;
  brandName?: string | null;
  description?: string | null;
  price?: number;
  cost?: number | null;
  category?: string;
  internalNotes?: string | null;
  sortOrder?: number | null;
  allowCustomPricing?: boolean;
  commissionable?: boolean;
  showInCheckOut?: boolean;
  isUsedAsTemplate?: boolean;
  isCompound?: boolean;
  isPopular?: boolean;
  showDoseQuantity?: boolean;
  ingredients?: CompoundIngredient[];
  dilutions?: MedicationDilution[];
  billingUnitRatio?: number;
  inventoryDrawdownTiming?: InventoryDrawdownTiming;
  defaultCptCode?: string | null;
  defaultModifiers?: string[] | null;
  defaultIcd10Codes?: string[] | null;
  defaultPlaceOfService?: string | null;
  // Template fields (only used when isUsedAsTemplate is true)
  templateAmount?: number | null;
  templateAdministrationLocation?: string | null;
  templateFrequency?: string | null;
  templateQuantity?: number | null;
  templateComment?: string | null;
  templateLotNumber?: string | null;
  exemptedTaxIds?: string[];
}
